import type {
	PrefetchContext,
	PrefetchedDataProps,
	PrefetchedUserDataProps,
	PrefetchUserContext,
} from '#src/shared/prefetch';

import { useLocale } from '#src/app/shared/hooks/l10n';
import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';

import { prefetchHomepagePageDataFromApi, useHomepagePageDataFromApi } from './pageData';
import { prefetchHomepagePageUserDataFromApi, useHomepagePageUserDataFromApi } from './pageUserData';

export type HomepagePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useHomepagePageDataFromApi>[0];
}>;

export type HomepagePagePrefetchedUserContext = NonNullable<Parameters<typeof useHomepagePageUserDataFromApi>[0]>;

export type HomepagePageCombinedState = MergedState<
	ReturnType<typeof useHomepagePageDataFromApi>,
	ReturnType<typeof useHomepagePageUserDataFromApi>
>;

export function usePageContext(
	prefetchedData?: HomepagePagePrefetchedContext,
	prefetchedUserData?: HomepagePagePrefetchedUserContext,
): HomepagePageCombinedState {
	const locale = useLocale();
	return useMergedStates(
		useHomepagePageDataFromApi(prefetchedData?.pageData, { locale }),
		useHomepagePageUserDataFromApi(prefetchedUserData),
	);
}

export async function prefetchPageContext(context: PrefetchContext): Promise<HomepagePagePrefetchedContext> {
	const pageData = await prefetchHomepagePageDataFromApi(context, {
		locale: context.l10n.locale,
	});

	return { pageData };
}

export async function prefetchPageUserContext(
	context: PrefetchUserContext,
): Promise<HomepagePagePrefetchedUserContext> {
	return prefetchHomepagePageUserDataFromApi(context);
}
export type HomepagePagePrefetchedDataProps = PrefetchedDataProps<HomepagePagePrefetchedContext> &
	PrefetchedUserDataProps<HomepagePagePrefetchedUserContext>;
